// src/features/settings/components/UpdateAvatarDialog.tsx
import React, { useState, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Button, Grid, Tabs, Tab, TextField, CircularProgress, Alert, Avatar, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CollectionsIcon from '@mui/icons-material/Collections';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import imageCompression from 'browser-image-compression';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { updateUserProfile, uploadAvatar, removeAvatar } from '../../auth/authSlice';
import { defaultAvatarIllustrations, FALLBACK_DEFAULT_AVATAR_URL } from '../../../config/avatarConfig';
import { User } from '../../../types';

// Helper function to create a default crop
function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
    return centerCrop(
        makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
        mediaWidth,
        mediaHeight,
    );
}

// Helper to get cropped image data
async function getCroppedImg(image: HTMLImageElement, crop: PixelCrop, rotation = 0): Promise<Blob | null> {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = 360; // Final output size
    canvas.height = 360;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('No 2d context');
    }

    ctx.save();
    // Translate and rotate around the center of the cropped area
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0, 0, 360, 360
    );

    ctx.restore();

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob);
        }, 'image/jpeg');
    });
}

// Helper to fetch image and convert to Base64
async function fetchImageAsBase64(url: string): Promise<string> {
    try {
        const response = await fetch(url, {
            headers: {
                'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/117.0.0.0 Safari/537.36',
                'Accept': 'image/*',
            },
            mode: 'cors',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to convert image to Base64'));
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Fetch image failed:', error);
        throw new Error('無法載入圖片');
    }
}

interface UpdateAvatarDialogProps {
    open: boolean;
    onClose: () => void;
    user: User;
}

type DialogView = 'main' | 'removeConfirm' | 'selectSource' | 'edit';

const UpdateAvatarDialog: React.FC<UpdateAvatarDialogProps> = ({ open, onClose, user }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { status, error } = useAppSelector(state => state.auth);

    const [view, setView] = useState<DialogView>('main');
    const [tabIndex, setTabIndex] = useState(0);
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [rotation, setRotation] = useState(0);
    const [webUrl, setWebUrl] = useState('');
    const [webUrlError, setWebUrlError] = useState('');

    const imgRef = useRef<HTMLImageElement>(null);
    const aspect = 1;

    const resetState = () => {
        setView('main');
        setTabIndex(0);
        setImgSrc('');
        setCrop(undefined);
        setCompletedCrop(undefined);
        setRotation(0);
        setWebUrl('');
        setWebUrlError('');
    };

    const handleClose = () => {
        onClose();
        // Delay reset to avoid visual glitch while dialog closes
        setTimeout(resetState, 300);
    };

    const handleBack = () => {
        if (view === 'edit') setView('selectSource');
        if (view === 'selectSource' || view === 'removeConfirm') setView('main');
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleIllustrationSelect = (url: string) => {
        setImgSrc(url);
        setView('edit');
    };

    const handleUrlApply = async () => {
        const imageUrlRegex = /^https?:\/\/.*\.(?:png|jpg|jpeg|gif|svg|webp)(?:\?.*)?$/i;
        // if (!webUrl || !imageUrlRegex.test(webUrl)) {
        if (!webUrl) {
            setWebUrlError(t('invalid_url_or_image'));
            return;
        }
        try {
            const base64Image = await fetchImageAsBase64(webUrl);
            setWebUrlError('');
            setImgSrc(base64Image);
            setView('edit');
        } catch (error) {
            setWebUrlError(t('failed_to_load_image'));
        }
    };

    const handleFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            setCrop(undefined); // Reset crop when new file is selected
            const reader = new FileReader();
            reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
            reader.readAsDataURL(files[0]);
            setView('edit');
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (aspect) {
            const { width, height } = e.currentTarget;
            setCrop(centerAspectCrop(width, height, aspect));
        }
    };

    const handleRemove = async () => {
        const result = await dispatch(removeAvatar());
        if (removeAvatar.fulfilled.match(result)) {
            handleClose();
        }
    };

    const handleUpload = async () => {
        // Case 1: Illustration selected - just update the URL
        if (defaultAvatarIllustrations.includes(imgSrc) || imgSrc.startsWith('/assets/avatars/')) {
            const result = await dispatch(updateUserProfile({ name: user.name, avatarUrl: imgSrc }));
            if (updateUserProfile.fulfilled.match(result)) {
                handleClose();
            }
            return;
        }

        // Case 2: Custom image - crop, compress, and upload
        if (completedCrop && imgRef.current) {
            try {
                const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop, rotation);
                if (!croppedImageBlob) {
                    setWebUrlError(t('failed_to_process_image'));
                    return;
                }

                const compressedFile = await imageCompression(croppedImageBlob as File, {
                    maxSizeMB: 0.5, // Compress if larger than 0.5MB
                    maxWidthOrHeight: 360,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });

                const formData = new FormData();
                formData.append('avatar', compressedFile, 'avatar.jpg');

                const result = await dispatch(uploadAvatar(formData));
                if (uploadAvatar.fulfilled.match(result)) {
                    handleClose();
                }
            } catch (error) {
                console.error('Image processing failed:', error);
                setWebUrlError(t('failed_to_process_image'));
            }
        }
    };

    const renderMainView = () => (
        <>
            <DialogTitle sx={{ textAlign: 'center', pt: 2, pb: 1 }}>{t('profile_photo')}</DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('profile_photo_desc')}
                </Typography>
                <Avatar src={user.avatarUrl} sx={{ width: 180, height: 180, m: 'auto', mb: 3, fontSize: '4rem' }}>
                    {user.name.charAt(0).toUpperCase()}
                </Avatar>
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1, mb: 1 }}>
                    <Button variant="contained" startIcon={<EditIcon />} onClick={() => setView('selectSource')}>
                        {t('change')}
                    </Button>
                    <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setView('removeConfirm')}>
                        {t('remove')}
                    </Button>
                </Stack>
            </DialogContent>
        </>
    );

    const renderRemoveConfirmView = () => (
        <>
            <DialogTitle sx={{ textAlign: 'center', pt: 2, pb: 1 }}>{t('remove_profile_photo_title')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{my: 2}}>
                    <Grid item textAlign="center" {...{} as any}>
                        <Typography variant="caption" color="text.secondary">{t('current')}</Typography>
                        <Avatar src={user.avatarUrl} sx={{ width: 100, height: 100, fontSize: '2.5rem' }}>{user.name.charAt(0).toUpperCase()}</Avatar>
                    </Grid>
                    <Grid item textAlign="center" {...{} as any}>
                        <Typography variant="caption" color="text.secondary">{t('new')}</Typography>
                        <Avatar src={FALLBACK_DEFAULT_AVATAR_URL} sx={{ width: 100, height: 100 }} />
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t('remove_profile_photo_desc')}
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{mt: 3, mb: 1}}>
                    <Button variant="outlined" onClick={handleBack}>{t('cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleRemove} disabled={status.user === 'loading'}>
                        {status.user === 'loading' ? <CircularProgress size={24} color="inherit" /> : t('remove')}
                    </Button>
                </Stack>
            </DialogContent>
        </>
    );

    const renderSelectSourceView = () => (
        <>
            <DialogTitle sx={{ textAlign: 'center', pt: 2, pb: 1 }}>{t('change_profile_photo_title')}</DialogTitle>
            <DialogContent>
                <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" centered>
                    <Tab icon={<CollectionsIcon />} label={t('illustrations')} />
                    <Tab icon={<AddLinkIcon />} label={t('from_the_web')} />
                    <Tab icon={<UploadFileIcon />} label={t('from_computer')} />
                </Tabs>

                {/* Illustrations Tab */}
                {tabIndex === 0 && (
                    <Box sx={{ p: 2, mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{mb: 2}}>{t('illustrations_desc')}</Typography>
                        <Grid container spacing={2}>
                            {defaultAvatarIllustrations.map((url, index) => (
                                <Grid item xs={3} key={index} sx={{cursor: 'pointer'}} {...{} as any}>
                                    <Avatar src={url} sx={{ width: '100%', height: 'auto' }} variant="rounded" onClick={() => handleIllustrationSelect(url)} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* From Web Tab */}
                {tabIndex === 1 && (
                    <Box sx={{ p: 2, mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{mb: 2}}>{t('photo_from_web_desc')}</Typography>
                        <TextField
                            fullWidth
                            label="Image URL"
                            value={webUrl}
                            onChange={(e) => setWebUrl(e.target.value)}
                            error={!!webUrlError}
                            helperText={webUrlError}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleUrlApply(); }}
                        />
                        <Button fullWidth variant="contained" sx={{mt: 2}} onClick={handleUrlApply}>{t('apply')}</Button>
                    </Box>
                )}

                {/* From Computer Tab */}
                {tabIndex === 2 && (
                    <Box onDragOver={handleDragOver} onDrop={handleDrop} sx={{ p: 2, mt: 2, mb: 1, border: '2px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                        <Avatar sx={{ width: 100, height: 100, m: 'auto', bgcolor: 'action.disabledBackground' }}>
                            <PhotoCamera sx={{ fontSize: 40, color: 'action.disabled' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{mt: 2}}>{t('drag_photo_here')}</Typography>
                        <Typography variant="body2" color="text.secondary">or</Typography>
                        <Button component="label" variant="contained" sx={{mt: 1, mb:1}}>
                            {t('upload_from_computer')}
                            <input type="file" hidden accept="image/*" onChange={(e) => handleFileSelect(e.target.files)} />
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </>
    );

    const renderEditView = () => (
        <>
            <DialogTitle sx={{ textAlign: 'center', pt: 2, pb: 1 }}>{t('edit_photo')}</DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'black', p:1, borderRadius: 1 }}>
                    {imgSrc && (
                        <ReactCrop
                            crop={crop}
                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={aspect}
                        >
                            <img
                                ref={imgRef}
                                alt="Crop me"
                                src={imgSrc}
                                style={{ transform: `rotate(${rotation}deg)` }}
                                onLoad={handleImageLoad}
                                onError={() => setWebUrlError(t('failed_to_load_image'))}
                            />
                        </ReactCrop>
                    )}
                </Box>
                <Button startIcon={<Rotate90DegreesCwIcon/>} onClick={() => setRotation(r => (r + 90) % 360)} sx={{mt: 2}}>Rotate</Button>
                <Button fullWidth variant="contained" sx={{mt: 2, mb:1}} onClick={handleUpload} disabled={!completedCrop || status.user === 'loading'}>
                    {status.user === 'loading' ? <CircularProgress size={24} color="inherit"/> : t('save_as_profile_photo')}
                </Button>
            </DialogContent>
        </>
    );

    const renderContent = () => {
        switch(view) {
            case 'main': return renderMainView();
            case 'removeConfirm': return renderRemoveConfirmView();
            case 'selectSource': return renderSelectSourceView();
            case 'edit': return renderEditView();
            default: return null;
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, pl:2 }}>
                {(view === 'main')
                    ? <Box/>
                    : <IconButton onClick={handleBack}><ArrowBackIcon /></IconButton>
                }
                <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </Box>
            {error && <Alert severity="error" sx={{mx: 2}}>{error}</Alert>}
            {renderContent()}
        </Dialog>
    );
};

export default UpdateAvatarDialog;